import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import LockIcon from '@material-ui/icons/Lock';
import { withStyles } from '@material-ui/core/styles';
import {
  confirmTotp,
  login as loginRequest,
  setupTotp,
  verifyTotpCode,
} from '../api';

const styles = theme => ({
  main: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: theme.palette.background.default,
  },
  card: {
    minWidth: 320,
    maxWidth: 480,
    marginTop: '6em',
  },
  avatarRow: {
    margin: '1em',
    display: 'flex',
    justifyContent: 'center',
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    padding: '0 1em 1em 1em',
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    marginBottom: '1em',
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: '0.5em',
  },
  qr: {
    display: 'block',
    margin: '1em auto',
    width: 220,
    height: 220,
  },
  secret: {
    fontFamily: 'monospace',
    background: theme.palette.grey[100],
    padding: '0.5em',
    wordBreak: 'break-all',
    marginBottom: '1em',
  },
  codes: {
    fontFamily: 'monospace',
    background: theme.palette.grey[100],
    padding: '0.75em',
    columns: 2,
    listStyle: 'none',
    margin: '0.5em 0',
  },
  warning: {
    color: theme.palette.error.main,
    margin: '0.5em 0',
  },
});

class Login extends Component {
  state = {
    // 'password' | 'totp' | 'enroll-setup' | 'enroll-confirm' | 'enroll-backup'
    step: 'password',
    username: '',
    password: '',
    code: '',
    stepUpToken: null,
    enrollData: null, // { secret, otpauthUrl, qrCodeDataUrl }
    backupCodes: null,
    pendingJwt: null,
    loading: false,
    error: null,
  };

  handleChange = field => event => {
    this.setState({ [field]: event.target.value });
  };

  handlePasswordSubmit = async event => {
    event.preventDefault();
    const { username, password } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const { data } = await loginRequest({ username, password });
      if (!data) {
        this.setState({ loading: false, error: 'Invalid username or password.' });
        return;
      }
      if (data.requiresEnrollment) {
        // Forced enrollment — fetch QR immediately and show the setup card.
        try {
          const { data: setup } = await setupTotp({ stepUpToken: data.totpStepUpToken });
          this.setState({
            loading: false,
            step: 'enroll-setup',
            stepUpToken: data.totpStepUpToken,
            enrollData: setup,
            password: '',
            code: '',
          });
        } catch (err) {
          this.setState({ loading: false, error: extractMessage(err) });
        }
        return;
      }
      if (data.requiresTotp) {
        this.setState({
          loading: false,
          step: 'totp',
          stepUpToken: data.totpStepUpToken,
          password: '',
        });
        return;
      }
      this.completeLogin(data.jsonWebToken);
    } catch (err) {
      this.setState({ loading: false, error: extractMessage(err) });
    }
  };

  handleTotpSubmit = async event => {
    event.preventDefault();
    const { code, stepUpToken } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const { data } = await verifyTotpCode({ code, stepUpToken });
      if (!data || !data.jsonWebToken) {
        this.setState({ loading: false, error: 'Invalid code.' });
        return;
      }
      this.completeLogin(data.jsonWebToken);
    } catch (err) {
      this.setState({ loading: false, error: extractMessage(err) });
    }
  };

  handleEnrollConfirm = async event => {
    event.preventDefault();
    const { code, stepUpToken } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const { data } = await confirmTotp({ code, stepUpToken });
      if (!data || !data.jsonWebToken) {
        this.setState({ loading: false, error: 'Enrollment failed. Try again.' });
        return;
      }
      this.setState({
        loading: false,
        step: 'enroll-backup',
        backupCodes: data.backupCodes,
        pendingJwt: data.jsonWebToken,
        enrollData: null,
        code: '',
      });
    } catch (err) {
      this.setState({ loading: false, error: extractMessage(err) });
    }
  };

  handleEnrollContinue = () => {
    const { pendingJwt } = this.state;
    if (pendingJwt) this.completeLogin(pendingJwt);
  };

  completeLogin(jsonWebToken) {
    window.localStorage.setItem('jsonWebToken', jsonWebToken);
    window.location.replace('/');
  }

  handleBack = () => {
    this.setState({
      step: 'password',
      code: '',
      stepUpToken: null,
      enrollData: null,
      backupCodes: null,
      pendingJwt: null,
      error: null,
    });
  };

  render() {
    const { classes } = this.props;
    const {
      step,
      username,
      password,
      code,
      loading,
      error,
      enrollData,
      backupCodes,
    } = this.state;

    return (
      <div className={classes.main}>
        <Card className={classes.card}>
          <div className={classes.avatarRow}>
            <Avatar className={classes.avatar}>
              <LockIcon />
            </Avatar>
          </div>
          <CardContent>
            {step === 'password' && (
              <form onSubmit={this.handlePasswordSubmit} className={classes.form}>
                <TextField
                  label="Username"
                  value={username}
                  onChange={this.handleChange('username')}
                  className={classes.input}
                  autoFocus
                  disabled={loading}
                  required
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={this.handleChange('password')}
                  className={classes.input}
                  disabled={loading}
                  required
                />
                {error && (
                  <Typography variant="body2" className={classes.error}>
                    {error}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={20} /> : 'Sign in'}
                </Button>
              </form>
            )}

            {step === 'totp' && (
              <form onSubmit={this.handleTotpSubmit} className={classes.form}>
                <Typography variant="body2" style={{ marginBottom: '1em' }}>
                  Enter the 6-digit code from your authenticator app, or one of your backup codes.
                </Typography>
                <TextField
                  label="Code"
                  value={code}
                  onChange={this.handleChange('code')}
                  className={classes.input}
                  autoFocus
                  disabled={loading}
                  required
                  inputProps={{ autoComplete: 'one-time-code' }}
                />
                {error && (
                  <Typography variant="body2" className={classes.error}>
                    {error}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={20} /> : 'Verify'}
                </Button>
                <Button onClick={this.handleBack} disabled={loading} style={{ marginTop: '0.5em' }}>
                  Back
                </Button>
              </form>
            )}

            {step === 'enroll-setup' && enrollData && (
              <form onSubmit={this.handleEnrollConfirm} className={classes.form}>
                <Typography variant="h6" style={{ marginBottom: '0.5em' }}>
                  Set up two-factor authentication
                </Typography>
                <Typography variant="body2" style={{ marginBottom: '0.5em' }}>
                  2FA is required to use the backoffice. Scan this QR code with your authenticator
                  app (Google Authenticator, Authy, 1Password), then enter the 6-digit code it
                  generates.
                </Typography>
                <img className={classes.qr} src={enrollData.qrCodeDataUrl} alt="2FA QR code" />
                <Typography variant="body2">If you cannot scan, enter this secret manually:</Typography>
                <div className={classes.secret}>{enrollData.secret}</div>
                <TextField
                  label="6-digit code"
                  value={code}
                  onChange={this.handleChange('code')}
                  className={classes.input}
                  autoFocus
                  disabled={loading}
                  required
                  inputProps={{ autoComplete: 'one-time-code', maxLength: 6 }}
                />
                {error && (
                  <Typography variant="body2" className={classes.error}>
                    {error}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !code}
                  fullWidth
                >
                  {loading ? <CircularProgress size={20} /> : 'Confirm and continue'}
                </Button>
                <Button onClick={this.handleBack} disabled={loading} style={{ marginTop: '0.5em' }}>
                  Cancel
                </Button>
              </form>
            )}

            {step === 'enroll-backup' && backupCodes && (
              <div className={classes.form}>
                <Typography variant="h6">Save your backup codes</Typography>
                <Typography variant="body2" className={classes.warning}>
                  Save these codes somewhere safe NOW. They will not be shown again. Each code can
                  be used once if you lose access to your authenticator.
                </Typography>
                <ul className={classes.codes}>
                  {backupCodes.map(c => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleEnrollContinue}
                  fullWidth
                >
                  I have saved them — continue
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
}

Login.propTypes = {
  classes: PropTypes.object.isRequired,
};

function extractMessage(err) {
  const message = (err && err.message) || 'Unknown error';
  const prefix = 'GraphQL Error: ';
  if (message.startsWith(prefix)) {
    try {
      const errors = JSON.parse(message.slice(prefix.length));
      if (Array.isArray(errors) && errors[0] && errors[0].message) {
        return errors[0].message;
      }
    } catch (e) {
      // fall through
    }
  }
  return message;
}

export default withRouter(withStyles(styles)(Login));
